import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Preview as PreviewIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { logger } from '../../utils/logger';
import { en } from '../../translations/en';
import api from '../../utils/api';

interface FieldOption {
  name: string;
  label: string;
  category: string;
}

interface PreviewResult {
  totalUsers: number;
  filteredUsers: number;
}

const ROLE_OPTIONS = ['basic', 'catalog_owner', 'commercial'];
const PLAN_OPTIONS = ['free', 'catalog_owner_monthly', 'commercial_monthly'];

const UserExportTab: React.FC = () => {
  const [fields, setFields] = useState<FieldOption[]>([]);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [filterRoles, setFilterRoles] = useState<string[]>([]);
  const [filterPlans, setFilterPlans] = useState<string[]>([]);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const t = en.admin.userExport;

  const fetchFields = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/users/export/fields');
      const fieldList: FieldOption[] = response.data.fields;
      setFields(fieldList);
      setSelectedFields(new Set(fieldList.map((f) => f.name)));
    } catch (err) {
      logger.error('Failed to fetch export fields', err);
      setError(t.exportError);
    } finally {
      setLoading(false);
    }
  }, [t.exportError]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const handleFieldToggle = (fieldName: string) => {
    setSelectedFields((prev) =>
      prev.has(fieldName)
        ? new Set([...prev].filter((f) => f !== fieldName))
        : new Set([...prev, fieldName])
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(new Set(fields.map((f) => f.name)));
  };

  const handleSelectNone = () => {
    setSelectedFields(new Set());
  };

  const buildFilters = () => {
    const filters: Record<string, unknown> = {};
    if (filterRoles.length > 0) filters.roles = filterRoles;
    if (filterPlans.length > 0) filters.subscriptionPlans = filterPlans;
    if (createdAfter) filters.createdAfter = createdAfter;
    if (createdBefore) filters.createdBefore = createdBefore;
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const response = await api.post('/api/admin/users/export/preview', {
        fields: Array.from(selectedFields),
        filters: buildFilters(),
      });
      setPreview({
        totalUsers: response.data.totalUsers,
        filteredUsers: response.data.filteredUsers,
      });
    } catch (err) {
      logger.error('Failed to preview export', err);
      setError(t.exportError);
    } finally {
      setPreviewing(false);
    }
  };

  const handleExport = async () => {
    setConfirmOpen(false);
    setExporting(true);
    setError(null);
    try {
      const response = await api.post(
        '/api/admin/users/export/csv',
        {
          fields: Array.from(selectedFields),
          filters: buildFilters(),
        },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? (disposition.split('filename=')[1]?.replace(/"/g, '') ?? 'user-export.csv')
        : 'user-export.csv';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to export CSV', err);
      setError(t.exportError);
    } finally {
      setExporting(false);
    }
  };

  const categories = fields.reduce<Record<string, FieldOption[]>>(
    (acc, field) => ({
      ...acc,
      [field.category]: [...(acc[field.category] ?? []), field],
    }),
    {}
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{t.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.description}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Field Selection */}
      <Typography variant="subtitle1" gutterBottom>{t.fields.title}</Typography>
      <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
        <Button size="small" onClick={handleSelectAll}>{t.fields.selectAll}</Button>
        <Button size="small" onClick={handleSelectNone}>{t.fields.selectNone}</Button>
      </Box>

      {Object.entries(categories).map(([category, categoryFields]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
            {t.fields.categories[category as keyof typeof t.fields.categories] ?? category}
          </Typography>
          <FormGroup row>
            {categoryFields.map((field) => (
              <FormControlLabel
                key={field.name}
                control={
                  <Checkbox
                    checked={selectedFields.has(field.name)}
                    onChange={() => handleFieldToggle(field.name)}
                    size="small"
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>
        </Box>
      ))}

      {/* Filters */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>{t.filters.title}</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          select
          label={t.filters.roles}
          value={filterRoles}
          onChange={(e) => setFilterRoles(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
          sx={{ minWidth: 200 }}
          SelectProps={{ multiple: true, renderValue: (selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((v) => <Chip key={v} label={v} size="small" />)}
            </Box>
          )}}
        >
          {ROLE_OPTIONS.map((role) => (
            <MenuItem key={role} value={role}>{role}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={t.filters.subscriptionPlans}
          value={filterPlans}
          onChange={(e) => setFilterPlans(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
          sx={{ minWidth: 200 }}
          SelectProps={{ multiple: true, renderValue: (selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((v) => <Chip key={v} label={v} size="small" />)}
            </Box>
          )}}
        >
          {PLAN_OPTIONS.map((plan) => (
            <MenuItem key={plan} value={plan}>{plan}</MenuItem>
          ))}
        </TextField>

        <TextField
          type="date"
          label={t.filters.createdAfter}
          value={createdAfter}
          onChange={(e) => setCreatedAfter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText={t.filters.createdAfterHelp}
          sx={{ minWidth: 160 }}
          InputProps={createdAfter ? {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear start date"
                  size="small"
                  onClick={() => setCreatedAfter('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          } : undefined}
        />

        <TextField
          type="date"
          label={t.filters.createdBefore}
          value={createdBefore}
          onChange={(e) => setCreatedBefore(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText={t.filters.createdBeforeHelp}
          sx={{ minWidth: 160 }}
          InputProps={createdBefore ? {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear end date"
                  size="small"
                  onClick={() => setCreatedBefore('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          } : undefined}
        />
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={previewing ? <CircularProgress size={16} /> : <PreviewIcon />}
          onClick={handlePreview}
          disabled={selectedFields.size === 0 || previewing}
        >
          {t.preview}
        </Button>

        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={selectedFields.size === 0 || exporting}
        >
          {exporting ? t.exporting : t.export}
        </Button>

        {preview && (
          <Chip
            label={`${preview.filteredUsers} / ${preview.totalUsers} ${t.previewResult}`}
            color="info"
            variant="outlined"
          />
        )}
      </Box>

      {selectedFields.size === 0 && (
        <Typography variant="caption" color="error">{t.noFields}</Typography>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t.confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t.confirmMessage}
            {preview && ` (${preview.filteredUsers} users)`}
          </DialogContentText>
          <Box sx={{ mt: 1 }}>
            {Array.from(selectedFields).map((f) => (
              <Chip key={f} label={f} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t.cancel}</Button>
          <Button onClick={handleExport} variant="contained">{t.confirm}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserExportTab;
